import _isEmpty from 'lodash/isEmpty';
import { selectProductsByDesignId } from '../store/cabinetVisualizer/selectors';
import { CABINET_SKU } from '../components/Collections/cabinetConstants';

export const getAllProducts = ({ allDesignProducts }) => (
  allDesignProducts ? Object.values(allDesignProducts)
    .flatMap((designProducts) => designProducts) : []);

export const getTotalCost = ({ allDesignProducts, itemPrices }) => getAllProducts({ allDesignProducts })
  .reduce((acc, product) => {
    const itemPrice = itemPrices.find((price) => product.itemId === price.itemId)?.price || 0;
    return acc + (itemPrice * product.quantity);
  }, 0);

export const getPartsOfTotalCost = ({ totalCost }) => {
  const fixed = Number(totalCost).toFixed(2);
  const [dollars, cents] = fixed.split('.');

  return { dollars, cents };
};

export const getCabinetProjectProducts = (designs, cabinetVisualizer) => {
  let allDesignProducts = Object.values(designs || {}).reduce((acc, design) => {
    if (design && design.designId && design.products) {
      acc[design.designId] = design.products;
    }
    return acc;
  }, {});

  if (_isEmpty(allDesignProducts) && !_isEmpty(cabinetVisualizer)) {
    allDesignProducts = selectProductsByDesignId(cabinetVisualizer, cabinetVisualizer.styleDesignIds || []);
  }
  const allProducts = allDesignProducts ? Object.values(allDesignProducts)
    .flatMap((designProducts) => designProducts) : [];

  return {
    allDesignProducts,
    allProducts,
  };
};

export const getMergedProducts = (allProducts, searchproducts) => {
  if (_isEmpty(allProducts) || _isEmpty(searchproducts)) return allProducts;

  const newMergedProducts = allProducts.map((product) => {
    const matchedSku = searchproducts
      .find((item) => Number(item.itemId) === Number(product.itemId));
    if (matchedSku && matchedSku.pricing) {
      return {
        ...product,
        pricing: matchedSku.pricing
      };
    }
    return product;
  });
  return newMergedProducts;
};

export const getTotalPriceData = (allProducts, searchproducts) => {
  const allProductsWithPricing = getMergedProducts(allProducts, searchproducts);
  if (_isEmpty(allProductsWithPricing)) return {};

  let originalPrice = 0;
  let dollarOff = 0;
  let value = 0;

  allProductsWithPricing.forEach((product) => {
    const pricing = product.pricing || null;
    if (pricing) {
      originalPrice += (pricing.original || 0) * (product.quantity || 1);

      if (pricing.promotion?.dollarOff) {
        dollarOff += (pricing.promotion?.dollarOff || 0) * (product.quantity || 1);
      }

      value += (pricing.value || 0) * (product.quantity || 1);
    }

  });

  const percentOff = Math.abs(Math.round(Number((dollarOff / originalPrice) * 100)));
  return {
    originalPrice, dollarOff, percentOff, value, itemId: null
  };
};

export const getHasConditionalPromotions = ({ product }) => {
  const hasConditonalPromo = !!product?.pricing?.conditionalPromotions?.some((promo) => promo.promotionId);

  return hasConditonalPromo;
};

/**
 * Finds a cabinet by ID, excluding void spacers
 * @param {Array} cabinets - Array of cabinet objects
 * @param {string} id - Cabinet ID to search for
 * @returns {Object|undefined} Found cabinet object or undefined if not found
 */
export const findCabinetByIdExcludeVoidSpacer = (cabinets, id) => {
  return cabinets.find((cab) => cab.id === id && cab.Sku !== CABINET_SKU.VOID_SPACE);
};

/**
 * Checks if a cabinet matches the given ID and is not a void spacer
 * @param {Object} cabinet - Cabinet object to check
 * @param {string} id - ID to match against
 * @returns {boolean} True if cabinet matches ID and is not a void spacer
 */
export const isCabinetMatchExcludeVoidSpacer = (cabinet, id) => {
  return cabinet.id === id && cabinet.Sku !== CABINET_SKU.VOID_SPACE;
};
